import { supabase } from "@/lib/supabase";
import { Project } from "@/lib/types";

interface CreateProjectResult {
  success: boolean;
  project?: Project;
  error?: string;
}

interface UpdateProjectResult {
  success: boolean;
  project?: Project;
  error?: string;
}

export const createProject = async (
  name: string,
  description?: string,
  image_url?: string
): Promise<CreateProjectResult> => {
  try {
    // Get the current authenticated user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('projects')
      .insert({
        name,
        description,
        image_url,
        user_id: user.id, // This is crucial for RLS policy
      })
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      project: data
    };
  } catch (error) {
    console.error("Create project error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

export const updateProject = async (
  projectId: string,
  updates: Partial<Pick<Project, 'name' | 'description' | 'is_archived' | 'image_url'>>
): Promise<UpdateProjectResult> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      project: data
    };
  } catch (error) {
    console.error("Update project error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

export const deleteProject = async (projectId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error("Delete project error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

export const getUserProjects = async (): Promise<(Project & { preview_count: number })[]> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        previews:previews(count)
      `)
      .order('updated_at', { ascending: false });

    if (error) throw error;

    // Transform the data to include preview count
    const projectsWithCounts = data?.map(project => ({
      ...project,
      preview_count: project.previews?.[0]?.count || 0
    })) || [];

    return projectsWithCounts;
  } catch (error) {
    console.error("Error fetching projects:", error);
    return [];
  }
};

export const getProjectById = async (projectId: string): Promise<Project | null> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();
    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching project by ID:", error);
    return null;
  }
};

export const getProjectPreviews = async (projectId: string) => {
  try {
    const { data, error } = await supabase
      .from('previews')
      .select('*, model:available_models(name), project:projects(name)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error("Error fetching project previews:", error);
    return [];
  }
};
